import React from 'react';
import { format, isToday, isTomorrow, isYesterday, isThisWeek, isPast, startOfToday } from 'date-fns';
import { AlertTriangle, Clock, CheckCircle2, Calendar } from 'lucide-react';

export function formatDueDate(dueDate: string | undefined | null, taskStatus: string, t?: any) {
  if (!dueDate) return null;
  
  const date = new Date(dueDate);
  const today = startOfToday();

  // Task is completed, show normal date
  if (taskStatus === 'completed') {
    return (
      <span className="text-gray-500">
        {format(date, 'MMM d, yyyy')}
      </span>
    );
  }

  // Task is overdue
  if (isPast(date) && date < today) {
    return (
      <div className="flex items-center">
        <Calendar className="h-4 w-4 mr-1 text-red-500" />
        <span className="text-red-500">
          {format(date, 'MMM d, yyyy')}
        </span>
      </div>
    );
  }

  // Task is due today
  if (isToday(date)) {
    return (
      <div className="flex items-center">
        <Calendar className="h-4 w-4 mr-1 text-orange-500" />
        <span className="text-orange-500 font-medium">
          {t?.dueDate?.today || 'Today'}
        </span>
      </div>
    );
  }

  // Task is due tomorrow
  if (isTomorrow(date)) {
    return (
      <div className="flex items-center">
        <Calendar className="h-4 w-4 mr-1 text-green-500" />
        <span className="text-green-500 font-medium">
          {t?.dueDate?.tomorrow || 'Tomorrow'}
        </span>
      </div>
    );
  }

  // Task was due yesterday
  if (isYesterday(date)) {
    return (
      <div className="flex items-center">
        <Calendar className="h-4 w-4 mr-1 text-red-500" />
        <span className="text-red-500">
          {t?.dueDate?.yesterday || 'Yesterday'}
        </span>
      </div>
    );
  }

  // Task is due this week
  if (isThisWeek(date, { weekStartsOn: 1 })) {
    return (
      <div className="flex items-center">
        <Calendar className="h-4 w-4 mr-1 text-gray-600" />
        <span className="text-gray-600">
          {format(date, 'EEEE')}
        </span>
      </div>
    );
  }

  // Default date format
  return (
    <div className="flex items-center">
      <Calendar className="h-4 w-4 mr-1 text-gray-400" />
      <span className={isPast(date) ? "text-red-500" : "text-gray-500"}>
        {format(date, 'MMM d, yyyy')}
      </span>
    </div>
  );
}

export function getPriorityIcon(priority: string) {
  switch (priority) {
    case 'urgent':
      return <AlertTriangle className="h-3 w-3 text-red-500" />;
    case 'high':
      return <Clock className="h-3 w-3 text-orange-500" />;
    case 'medium':
      return <Clock className="h-3 w-3 text-yellow-500" />;
    case 'low':
      return <CheckCircle2 className="h-3 w-3 text-green-500" />;
    default:
      return null;
  }
}

export function getStatusClass(status: string) {
  switch (status) {
    case 'todo':
      return 'bg-gray-100 text-gray-800';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800';
    case 'review':
      return 'bg-yellow-100 text-yellow-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'standby':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getPriorityClass(priority: string) {
  switch (priority) {
    case 'urgent':
      return 'bg-red-100 text-red-800';
    case 'high':
      return 'bg-orange-100 text-orange-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'low':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function getPriorityText(priority: string) {
  switch (priority) {
    case 'urgent':
      return 'URGENTE';
    case 'high':
      return 'ALTA';
    case 'medium':
      return 'MEDIA';
    case 'low':
      return 'BASSA';
    default:
      return 'BASSA';
  }
}

export function getStatusText(status: string) {
  switch (status) {
    case 'todo':
      return 'Da Fare';
    case 'in_progress':
      return 'In Corso';
    case 'review':
      return 'In Revisione';
    case 'completed':
      return 'Completato';
    case 'standby':
      return 'In attesa';
    default:
      return 'Da Fare';
  }
}

// Funzioni per il tracciamento delle attività in italiano
export function createActivityComment(activityType: string, details: any, user: any): string {
  const userName = user?.full_name || user?.email || 'Utente sconosciuto';
  const timestamp = new Date().toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  switch (activityType) {
    case 'status_change':
      return `**${userName}** ha cambiato lo stato da **${getStatusText(details.oldStatus)}** a **${getStatusText(details.newStatus)}** il ${timestamp}`;
    
    case 'priority_change':
      return `**${userName}** ha cambiato la priorità da **${getPriorityText(details.oldPriority)}** a **${getPriorityText(details.newPriority)}** il ${timestamp}`;
    
    case 'assignee_change':
      if (details.oldAssignee && details.newAssignee) {
        return `**${userName}** ha riassegnato il task da **${details.oldAssignee}** a **${details.newAssignee}** il ${timestamp}`;
      } else if (details.newAssignee) {
        return `**${userName}** ha assegnato il task a **${details.newAssignee}** il ${timestamp}`;
      } else {
        return `**${userName}** ha rimosso l'assegnazione del task il ${timestamp}`;
      }
    
    case 'due_date_change':
      if (details.oldDueDate && details.newDueDate) {
        const oldDate = new Date(details.oldDueDate).toLocaleDateString('it-IT');
        const newDate = new Date(details.newDueDate).toLocaleDateString('it-IT');
        return `**${userName}** ha cambiato la scadenza dal ${oldDate} al ${newDate} il ${timestamp}`;
      } else if (details.newDueDate) {
        const newDate = new Date(details.newDueDate).toLocaleDateString('it-IT');
        return `**${userName}** ha impostato la scadenza al ${newDate} il ${timestamp}`;
      } else {
        return `**${userName}** ha rimosso la scadenza il ${timestamp}`;
      }
    
    case 'title_change':
      return `**${userName}** ha cambiato il titolo da "**${details.oldTitle}**" a "**${details.newTitle}**" il ${timestamp}`;
    
    case 'description_change':
      return `**${userName}** ha aggiornato la descrizione il ${timestamp}`;
    
    case 'section_change':
      return `**${userName}** ha spostato il task nella sezione **${getSectionText(details.newSection)}** il ${timestamp}`;
    
    case 'task_created':
      return `**${userName}** ha creato il task il ${timestamp}`;
    
    case 'task_completed':
      return `**${userName}** ha completato il task il ${timestamp}`;
    
    case 'task_reopened':
      return `**${userName}** ha riaperto il task il ${timestamp}`;
    
    case 'attachment_added':
      return `**${userName}** ha aggiunto l'allegato **${details.fileName}** il ${timestamp}`;
    
    case 'attachment_removed':
      return `**${userName}** ha rimosso l'allegato **${details.fileName}** il ${timestamp}`;
    
    case 'checklist_item_added':
      return `**${userName}** ha aggiunto un elemento alla checklist: "**${details.itemTitle}**" il ${timestamp}`;
    
    case 'checklist_item_completed':
      return `**${userName}** ha completato l'elemento della checklist: "**${details.itemTitle}**" il ${timestamp}`;
    
    case 'checklist_item_uncompleted':
      return `**${userName}** ha rimarcato come non completato l'elemento della checklist: "**${details.itemTitle}**" il ${timestamp}`;
    
    case 'checklist_item_removed':
      return `**${userName}** ha rimosso l'elemento dalla checklist: "**${details.itemTitle}**" il ${timestamp}`;
    
    default:
      return `**${userName}** ha effettuato un'azione sul task il ${timestamp}`;
  }
}

export function getSectionText(section: string) {
  switch (section) {
    case 'todo':
      return 'Da Fare';
    case 'backlog':
      return 'Backlog';
    case 'sprint':
      return 'Sprint';
    case 'in_progress':
      return 'In Corso';
    case 'review':
      return 'In Revisione';
    case 'completed':
      return 'Completati';
    default:
      return section;
  }
}